/**
 * Skills and elements. A move sounds as `skill.fx.<fx>` (its `SkillDef.fx` id) when one is
 * registered here, and otherwise as its element, `skill.el.<element>` (sfx/events.ts). A new skill
 * effect gets its own sound by registering `skill.fx.<its fx>`.
 *
 * Also: the ice of เหมันต์สะกดธาร (crystals shattering, frost creeping, the warp's whoosh), thunder, the
 * nine flying swords, and the chimes of healing and buffs.
 */
import type { Element } from '@nine/shared';
import { BELL, type SfxKit } from '../kit';
import { registerSfx } from '../registry';
import { bowl, chime, note, paper, ring, ROOT_D, whoosh } from '../voices';

// ───────── the five elements ─────────

const ELEMENTS: Record<Element, { label: string; play: (k: SfxKit) => void }> = {
  yin: {
    label: 'ธาตุหยิน (เย็น มืด)',
    play: (k) => {
      k.fm({ f: 660, f1: 440, ratio: 1.41, index: 3, index1: 0.1, dur: 0.9, vol: 0.09 });
      k.noise({ dur: 0.6, vol: 0.12, attack: 0.15, f: 1900, f1: 650, q: 8 });
      k.tone({ f: 110, dur: 0.8, vol: 0.12, attack: 0.1 });
    },
  },
  yang: {
    label: 'ธาตุหยาง (ร้อน สว่าง)',
    play: (k) => {
      k.noise({ color: 'crackle', dur: 0.6, vol: 0.5, attack: 0.05, type: 'highpass', f: 1300 });
      k.noise({ color: 'pink', dur: 0.5, vol: 0.18, attack: 0.08, f: 600, f1: 2400, q: 1.2 });
      k.tone({ type: 'sawtooth', f: 220, f1: 470, dur: 0.4, vol: 0.06, out: k.filter('bandpass', 1300, 2) });
      chime(k, { f: 1318.5, at: 0.06, vol: 0.06, dur: 0.6 });
    },
  },
  soft: {
    label: 'ธาตุอ่อน (น้ำ แพรไหม)',
    play: (k) => {
      k.tone({ f: 523, f1: 784, sweep: 0.25, dur: 0.7, vol: 0.08, attack: 0.04, vib: [5, 0.01] });
      k.tone({ f: 784, f1: 659, at: 0.18, dur: 0.6, vol: 0.06, attack: 0.04 });
      k.noise({ color: 'pink', dur: 0.6, vol: 0.12, attack: 0.15, f: 900, f1: 1500, q: 2, trem: [12, 0.7] });
    },
  },
  hard: {
    label: 'ธาตุแข็ง (ดิน หิน)',
    play: (k) => {
      k.thump({ f0: 95, f1: 38, dur: 0.4, vol: 0.45 });
      k.noise({ color: 'crackle', dur: 0.4, vol: 0.6, type: 'lowpass', f: 1400 });
      k.noise({ color: 'brown', dur: 0.4, vol: 0.35, type: 'lowpass', f: 500, f1: 120 });
      k.metal({ f: 220, dur: 0.6, vol: 0.06, partials: BELL });
    },
  },
  taiji: {
    label: 'ธาตุไท่จี๋ (สมดุล)',
    play: (k) => {
      // Two tones a fifth apart circling round the listener.
      const l = k.pan(0);
      k.lfo(l.pan, 2.2, 0.8, 0, 1.1);
      k.tone({ f: 440, dur: 1, vol: 0.07, attack: 0.08, out: l });
      const r = k.pan(0);
      k.lfo(r.pan, 2.2, -0.8, 0, 1.1);
      k.tone({ f: 660, dur: 1, vol: 0.05, attack: 0.08, out: r });
      bowl(k, { f: 220, dur: 1.4, vol: 0.06 });
      whoosh(k, { dur: 0.5, f0: 400, f1: 1400, f2: 600, vol: 0.1, q: 1.5 });
    },
  },
};

for (const [el, d] of Object.entries(ELEMENTS) as [Element, (typeof ELEMENTS)[Element]][])
  registerSfx(`skill.el.${el}`, { label: d.label, cat: 'skill', vary: 0.05, gap: 0.06 }, (k) => d.play(k));

// ───────── ice (เหมันต์สะกดธาร) ─────────

/** Crystals: glassy pings scattered high. */
function crystals(k: SfxKit, n: number, at: number, spread: number, vol: number): void {
  for (let i = 0; i < n; i++)
    k.tone({
      f: k.range(2800, 7200),
      at: at + k.r() * spread,
      dur: k.range(0.05, 0.25),
      vol: vol * k.range(0.5, 1),
    });
}

registerSfx('skill.ice.shatter', { label: 'น้ำแข็งแตก (ผลึก)', cat: 'skill', vary: 0.05, gap: 0.05 }, (k) => {
  k.noise({ dur: 0.08, vol: 0.3, attack: 0.001, type: 'highpass', f: 4000 });
  k.noise({ color: 'crackle', dur: 0.35, vol: 0.8, type: 'highpass', f: 3000 });
  crystals(k, 12, 0, 0.25, 0.05);
  k.thump({ f0: 200, f1: 90, dur: 0.12, vol: 0.2 });
});

registerSfx(
  'skill.ice.frost',
  { label: 'น้ำค้างแข็งเกาะ', cat: 'skill', vary: 0.04, gap: 0.15, preview: 1.4 },
  (k) => {
    // Frost creeping: a slow glassy shimmer and fine crackle growing.
    k.noise({ color: 'crackle', dur: 1.1, vol: 0.5, attack: 0.7, type: 'highpass', f: 5000 });
    k.noise({ dur: 1.1, vol: 0.05, attack: 0.6, f: 7000, q: 4 });
    for (let i = 0; i < 4; i++)
      k.tone({ f: note(1174.7, i * 2), at: i * 0.12, dur: 0.9, vol: 0.02, attack: 0.3 });
  },
);

registerSfx('skill.ice.warp', { label: 'วาร์ปหิมะ (ฟิ้ว)', cat: 'skill', vary: 0.05, gap: 0.05 }, (k) => {
  // A reversed whoosh: swelling in, cut off sharp where the swordsman reappears.
  k.noise({ dur: 0.22, vol: 0.3, attack: 0.2, f: 600, f1: 5200, q: 2.5, linear: true });
  k.noise({ at: 0.21, dur: 0.12, vol: 0.12, type: 'highpass', f: 5000 });
  k.tone({ f: 3520, at: 0.2, dur: 0.3, vol: 0.04 });
  crystals(k, 4, 0.2, 0.1, 0.03);
});

// ───────── heal and buffs ─────────

registerSfx(
  'skill.heal',
  { label: 'รักษา (ระฆังใส)', cat: 'skill', vary: 0.02, gap: 0.2, preview: 1.6 },
  (k) => {
    [0, 2, 4, 5].forEach((d, i) => chime(k, { f: note(587.33, d), at: i * 0.08, vol: 0.06, dur: 1.1 }));
    k.noise({ color: 'pink', dur: 1, vol: 0.06, attack: 0.4, f: 2000, f1: 4000, q: 1.5 });
  },
);

registerSfx('skill.buff', { label: 'เสริมพลัง', cat: 'skill', vary: 0.02, gap: 0.2 }, (k) => {
  k.tone({ f: 330, f1: 660, dur: 0.5, vol: 0.06, attack: 0.3 });
  k.noise({ dur: 0.5, vol: 0.1, attack: 0.4, f: 500, f1: 3000, q: 2 });
  chime(k, { f: 1318.5, at: 0.42, vol: 0.06 });
});

// ───────── each skill effect (SkillDef.fx) ─────────

type Fx = (k: SfxKit) => void;

const FX: Record<string, { label: string; play: Fx; preview?: number }> = {
  bolt: {
    label: 'สายฟ้า',
    preview: 1.8,
    play: (k) => {
      k.noise({ dur: 0.05, vol: 0.5, attack: 0.001, type: 'highpass', f: 2500 });
      k.noise({ color: 'crackle', dur: 0.35, vol: 0.9, type: 'highpass', f: 1800 });
      k.tone({ type: 'square', f: 82, f1: 38, dur: 0.3, vol: 0.06, out: k.filter('lowpass', 600) });
      k.noise({
        color: 'brown',
        at: 0.05,
        dur: 1.4,
        vol: 0.5,
        attack: 0.08,
        type: 'lowpass',
        f: 380,
        f1: 90,
        trem: [7, 0.6],
      });
    },
  },
  nine: {
    label: 'เก้ากระบี่เหิน',
    preview: 1.4,
    play: (k) => {
      // Nine swords leaving one after another, each with its own whistle and ring, up the scale.
      for (let n = 0; n < 9; n++) {
        const at = n * 0.055;
        whoosh(k, { at, dur: 0.22, f0: 1200, f1: 5000, f2: 2600, vol: 0.09, q: 4 });
        ring(k, { f: note(880, n), at: at + 0.03, dur: 0.3, vol: 0.022 });
      }
    },
  },
  calm: {
    label: 'จิตสงบ (รักษา)',
    preview: 1.6,
    play: (k) => {
      bowl(k, { f: 392, dur: 1.4, vol: 0.08 });
      [0, 2, 4].forEach((d, i) => chime(k, { f: note(784, d), at: 0.1 + i * 0.1, vol: 0.05 }));
    },
  },
  palm: {
    label: 'ฝ่ามือ',
    play: (k) => {
      k.thump({ f0: 130, f1: 45, dur: 0.3, vol: 0.45 });
      k.noise({ color: 'brown', dur: 0.35, vol: 0.4, attack: 0.02, type: 'lowpass', f: 700, f1: 200 });
      whoosh(k, { dur: 0.3, f0: 300, f1: 1300, f2: 500, vol: 0.16, q: 1.5 });
    },
  },
  pierce: {
    label: 'แทงทะลวง',
    play: (k) => {
      k.noise({ dur: 0.14, vol: 0.28, attack: 0.03, f: 1200, f1: 5200, q: 4 });
      k.tone({ f: 2600, f1: 3900, dur: 0.12, vol: 0.03 });
      k.click({ f: 3000, at: 0.12, vol: 0.12 });
    },
  },
  whirl: {
    label: 'ลมหมุน',
    play: (k) => {
      for (let n = 0; n < 3; n++)
        whoosh(k, { at: n * 0.11, dur: 0.22, f0: 500, f1: 2600, f2: 800, vol: 0.18, q: 1.8 });
    },
  },
  breath: {
    label: 'ลมหายใจพ่น',
    preview: 1.2,
    play: (k) => {
      k.noise({ color: 'pink', dur: 0.9, vol: 0.4, attack: 0.1, f: 900, f1: 400, q: 0.9 });
      k.tone({
        type: 'sawtooth',
        f: 90,
        f1: 70,
        dur: 0.8,
        vol: 0.06,
        attack: 0.08,
        out: k.filter('lowpass', 500),
      });
    },
  },
  bite: {
    label: 'งับ',
    play: (k) => {
      k.click({ f: 2400, vol: 0.4, q: 2 });
      k.noise({ color: 'crackle', dur: 0.15, vol: 0.6, type: 'bandpass', f: 1500, q: 0.8 });
      k.thump({ f0: 160, f1: 70, dur: 0.12, vol: 0.25 });
    },
  },
  slash: {
    label: 'คลื่นกระบี่',
    play: (k) => {
      whoosh(k, { dur: 0.26, f0: 800, f1: 4600, f2: 2000, vol: 0.3, q: 2.5 });
      ring(k, { f: 2640, at: 0.12, dur: 0.4, vol: 0.04 });
    },
  },
  crush: {
    label: 'ทุบบดขยี้',
    play: (k) => {
      k.thump({ f0: 110, f1: 32, dur: 0.45, vol: 0.5 });
      k.noise({ color: 'crackle', dur: 0.4, vol: 0.7, type: 'lowpass', f: 2200 });
      k.noise({ color: 'brown', dur: 0.4, vol: 0.4, type: 'lowpass', f: 600, f1: 100 });
    },
  },
  cleave: {
    label: 'ผ่าฟันใหญ่',
    play: (k) => {
      whoosh(k, { dur: 0.32, f0: 300, f1: 2200, f2: 700, vol: 0.34, q: 1.6 });
      whoosh(k, { dur: 0.34, f0: 120, f1: 500, f2: 160, vol: 0.2, q: 1, color: 'pink' });
      k.thump({ f0: 120, f1: 45, at: 0.18, dur: 0.25, vol: 0.3 });
    },
  },
  ring: {
    label: 'คลื่นวงแหวน',
    play: (k) => {
      k.thump({ f0: 140, f1: 50, dur: 0.3, vol: 0.4 });
      k.noise({ dur: 0.5, vol: 0.2, attack: 0.02, f: 400, f1: 2400, q: 2 });
      k.metal({ f: 520, dur: 0.6, vol: 0.06, partials: BELL });
    },
  },
  wind: {
    label: 'คมลม',
    preview: 1,
    play: (k) => {
      k.noise({
        color: 'pink',
        dur: 0.7,
        vol: 0.3,
        attack: 0.15,
        f: 500,
        f1: 1800,
        f2: 700,
        sweep: 0.35,
        q: 1.6,
        trem: [9, 0.4],
      });
      whoosh(k, { at: 0.1, dur: 0.25, f0: 1500, f1: 5000, f2: 2500, vol: 0.12, q: 3 });
    },
  },
  taiji: {
    label: 'ไท่จี๋',
    preview: 1.6,
    play: (k) => ELEMENTS.taiji.play(k),
  },
  snow: {
    label: 'กระบี่ธารเยือกแข็ง',
    play: (k) => {
      whoosh(k, { dur: 0.22, f0: 900, f1: 4800, f2: 2400, vol: 0.28, q: 3 });
      crystals(k, 6, 0.08, 0.15, 0.035);
    },
  },
  snowdash: {
    label: 'พุ่งหิมะ',
    play: (k) => {
      k.noise({ dur: 0.3, vol: 0.3, attack: 0.06, f: 500, f1: 3200, q: 1.6 });
      k.noise({ color: 'crackle', dur: 0.3, vol: 0.4, type: 'highpass', f: 3500 });
      crystals(k, 4, 0.1, 0.15, 0.03);
    },
  },
  blizzard: {
    label: 'พายุหิมะ',
    preview: 2,
    play: (k) => {
      k.noise({
        color: 'pink',
        dur: 1.6,
        vol: 0.32,
        attack: 0.3,
        f: 500,
        f1: 1400,
        f2: 600,
        sweep: 0.8,
        q: 2.5,
        trem: [3, 0.5],
      });
      k.noise({ dur: 1.5, vol: 0.06, attack: 0.4, f: 2600, f1: 3800, q: 6 });
      crystals(k, 16, 0.1, 1.2, 0.03);
    },
  },
  snowstep: {
    label: 'ก้าวหิมะ',
    play: (k) => {
      k.noise({ color: 'crackle', dur: 0.15, vol: 0.6, type: 'lowpass', f: 3500 });
      whoosh(k, { dur: 0.2, f0: 800, f1: 3000, f2: 1200, vol: 0.14, q: 2 });
      k.tone({ f: 2960, at: 0.05, dur: 0.3, vol: 0.03 });
    },
  },
  icebreak: {
    label: 'ทลายน้ำแข็ง',
    preview: 1,
    play: (k) => {
      k.thump({ f0: 120, f1: 40, dur: 0.35, vol: 0.4 });
      k.noise({ dur: 0.1, vol: 0.35, attack: 0.001, type: 'highpass', f: 3500 });
      k.noise({ color: 'crackle', dur: 0.6, vol: 0.9, type: 'highpass', f: 2500 });
      crystals(k, 18, 0, 0.45, 0.045);
    },
  },
  snowpride: {
    label: 'ความทะนงแห่งหิมะ',
    preview: 1.6,
    play: (k) => {
      k.noise({ dur: 1.2, vol: 0.12, attack: 1, f: 1200, f1: 6000, q: 5, linear: true });
      for (let i = 0; i < 6; i++)
        k.tone({ f: note(1174.7, i), at: i * 0.15, dur: 0.6, vol: 0.025, attack: 0.05 });
      k.noise({ color: 'crackle', at: 0.3, dur: 1, vol: 0.4, attack: 0.6, type: 'highpass', f: 4500 });
    },
  },
  snowblink: {
    label: 'วาร์ปหิมะต่อเนื่อง',
    play: (k) => {
      k.noise({ dur: 0.2, vol: 0.3, attack: 0.18, f: 600, f1: 5600, q: 2.5, linear: true });
      k.tone({ f: 3520, at: 0.18, dur: 0.35, vol: 0.04 });
      crystals(k, 5, 0.18, 0.12, 0.03);
    },
  },
  vajra: {
    label: 'วัชระ (ระฆังทองแดง)',
    preview: 2,
    play: (k) => {
      k.thump({ f0: 120, f1: 40, dur: 0.35, vol: 0.45 });
      k.metal({ f: 196, dur: 1.6, vol: 0.12, partials: BELL, drift: 0.005 });
      k.noise({ color: 'brown', dur: 0.3, vol: 0.3, type: 'lowpass', f: 700 });
    },
  },
  quake: {
    label: 'แผ่นดินไหว',
    preview: 1.8,
    play: (k) => {
      k.noise({
        color: 'brown',
        dur: 1.4,
        vol: 0.6,
        attack: 0.05,
        type: 'lowpass',
        f: 260,
        f1: 70,
        trem: [11, 0.6],
      });
      for (let n = 0; n < 3; n++) k.thump({ f0: 90, f1: 30, at: n * 0.2, dur: 0.4, vol: 0.35 });
      k.noise({ color: 'crackle', dur: 1.1, vol: 0.6, attack: 0.1, type: 'bandpass', f: 1200, q: 0.7 });
    },
  },
  bell: {
    label: 'ระฆังทอง (กายาทองคำ)',
    preview: 2.6,
    play: (k) => {
      k.metal({ f: 233, dur: 2.2, vol: 0.13, partials: BELL, drift: 0.004 });
      k.thump({ f0: 160, f1: 70, dur: 0.2, vol: 0.25 });
      k.noise({ color: 'pink', dur: 1, vol: 0.05, attack: 0.3, f: 1600, q: 3 });
    },
  },
  sabre: {
    label: 'คมดาบใหญ่',
    play: (k) => {
      whoosh(k, { dur: 0.3, f0: 350, f1: 2400, f2: 800, vol: 0.34, q: 1.6 });
      ring(k, { f: 1480, at: 0.15, dur: 0.5, vol: 0.05 });
      k.thump({ f0: 140, f1: 55, at: 0.16, dur: 0.2, vol: 0.25 });
    },
  },
  chain: {
    label: 'โซ่ฟาด',
    play: (k) => {
      whoosh(k, { dur: 0.26, f0: 500, f1: 2400, f2: 900, vol: 0.2, q: 1.8 });
      k.noise({ color: 'crackle', dur: 0.4, vol: 0.7, attack: 0.03, f: 3200, q: 1.5 });
      for (let n = 0; n < 7; n++)
        k.metal({ f: k.range(1800, 3400), at: 0.05 + n * 0.04, dur: 0.12, vol: 0.02 });
    },
  },
  aura: {
    label: 'ลมปราณปะทุ',
    preview: 1.2,
    play: (k) => {
      k.tone({ f: 110, f1: 220, dur: 0.8, vol: 0.12, attack: 0.3 });
      k.tone({ f: 165, f1: 330, dur: 0.8, vol: 0.07, attack: 0.3 });
      k.noise({ dur: 0.8, vol: 0.18, attack: 0.5, f: 300, f1: 2800, q: 1.8 });
    },
  },
  shadow: {
    label: 'เงามาร',
    play: (k) => {
      k.noise({ dur: 0.35, vol: 0.28, attack: 0.3, f: 300, f1: 1500, q: 2, linear: true });
      k.fm({ f: 220, f1: 165, ratio: 1.41, index: 4, dur: 0.6, vol: 0.06 });
      k.noise({ at: 0.3, dur: 0.3, vol: 0.08, f: 1800, f1: 600, q: 8 });
    },
  },
  needles: {
    label: 'เข็มพิษ',
    play: (k) => {
      for (let n = 0; n < 5; n++)
        k.tone({ f: k.range(4000, 5200), f1: k.range(2200, 2800), at: n * 0.035, dur: 0.12, vol: 0.035 });
      k.noise({ dur: 0.2, vol: 0.06, type: 'highpass', f: 6000 });
    },
  },
  brush: {
    label: 'พู่กันหมึก',
    play: (k) => {
      k.noise({
        color: 'pink',
        dur: 0.3,
        vol: 0.26,
        attack: 0.1,
        f: 900,
        f1: 2200,
        f2: 1100,
        sweep: 0.15,
        q: 1.4,
      });
      k.tone({ f: 300, f1: 900, at: 0.18, dur: 0.12, vol: 0.06 });
    },
  },
  ink: {
    label: 'หมึกสาด',
    play: (k) => {
      paper(k, { dur: 0.25, vol: 0.2 });
      k.noise({
        color: 'pink',
        dur: 0.35,
        vol: 0.28,
        attack: 0.08,
        f: 700,
        f1: 1800,
        f2: 600,
        sweep: 0.15,
        q: 1.2,
      });
      for (let n = 0; n < 3; n++)
        k.tone({ f: k.range(280, 420), f1: k.range(800, 1100), at: 0.15 + n * 0.05, dur: 0.08, vol: 0.05 });
    },
  },
  seal: {
    label: 'ตราผนึก',
    preview: 1.4,
    play: (k) => {
      k.thump({ f0: 220, f1: 80, dur: 0.15, vol: 0.4 });
      k.click({ f: 1400, vol: 0.25 });
      chime(k, { f: note(ROOT_D * 2, 4), at: 0.05, vol: 0.06, dur: 1 });
    },
  },
  grab: {
    label: 'คว้ากระชาก',
    play: (k) => {
      k.noise({ dur: 0.15, vol: 0.22, attack: 0.02, f: 1500, f1: 600, q: 1 });
      k.thump({ f0: 150, f1: 60, at: 0.1, dur: 0.18, vol: 0.32 });
    },
  },
  blood: {
    label: 'คมโลหิต',
    play: (k) => {
      whoosh(k, { dur: 0.24, f0: 600, f1: 3600, f2: 1400, vol: 0.28, q: 2.2 });
      k.noise({ color: 'brown', at: 0.12, dur: 0.22, vol: 0.4, f: 500, q: 1.5, trem: [30, 0.8] });
      k.tone({ f: 146.8, f1: 110, dur: 0.5, vol: 0.06, attack: 0.05 });
    },
  },
  bloodstorm: {
    label: 'พายุโลหิต',
    preview: 2,
    play: (k) => {
      k.tone({ type: 'sawtooth', f: 55, dur: 1.4, vol: 0.12, attack: 0.2, out: k.filter('lowpass', 400) });
      for (let n = 0; n < 5; n++)
        whoosh(k, { at: n * 0.2, dur: 0.24, f0: 500, f1: 3000, f2: 900, vol: 0.18, q: 2 });
      k.noise({ color: 'brown', dur: 1.4, vol: 0.3, attack: 0.2, f: 400, q: 1.2, trem: [22, 0.7] });
    },
  },
};

for (const [fx, d] of Object.entries(FX))
  registerSfx(
    `skill.fx.${fx}`,
    { label: d.label, cat: 'skill', vary: 0.04, gap: 0.05, preview: d.preview },
    (k) => d.play(k),
  );
